if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        this.form.querySelectorAll('[data-far-bench-all]').forEach(button => {
          button.addEventListener('click', () => {
            const scroller = this.form.querySelector('[data-far-bench-track]');
            if (!scroller) return;
            const expanded = scroller.classList.toggle('far-smart-upsells__bench-track--all');
            button.textContent = expanded ? button.dataset.textHide : button.dataset.textShow;
            scroller.scrollLeft = 0;
          });
        });

        this.form.querySelectorAll('[data-far-bench-scroll]').forEach(button => {
          button.addEventListener('click', () => {
            const scroller = this.form.querySelector('[data-far-bench-track]');
            scroller?.scrollBy({ left: Number(button.dataset.farBenchScroll) * 165, behavior: 'smooth' });
          });
        });

        if (theme.settings.afterAtc !== 'no-js') {
          this.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      const primaryId = Number(formData.get('id'));
      const requestedQuantity = Math.max(1, Number(formData.get('quantity')) || 1);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += ',' + this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');
      }

      try {
        const oldCartResponse = await fetch(theme.routes.cart + '.js');
        if (!oldCartResponse.ok) throw new Error('Cart could not be read');
        const oldCartData = await oldCartResponse.json();
        if (!Number.isSafeInteger(primaryId) || primaryId <= 0) throw new Error('Invalid product variant');

        // One combined cart request: optional items are independently selectable,
        // and already-present accessory variants are never duplicated.
        const existingVariants = new Set((oldCartData.items || []).map(item => String(item.variant_id)));
        const additions = [];
        const addOptional = (rawId, rawQuantity) => {
          const id = Number(rawId);
          const quantity = Math.min(100, Math.max(1, Number(rawQuantity) || 1));
          if (!Number.isSafeInteger(id) || id <= 0 || id === primaryId) return;
          if (existingVariants.has(String(id)) || additions.some(item => item.id === id)) return;
          additions.push({ id, quantity });
        };

        const matOptIn = this.form.querySelector('[data-far-cardio-mat-toggle]');
        const matVariant = this.form.querySelector('[data-far-cardio-mat-select]');
        if (matOptIn?.checked && matVariant?.value) {
          addOptional(matVariant.value, requestedQuantity);
        }

        this.form.querySelectorAll('[data-far-smart-item]').forEach(row => {
          const check = row.querySelector('[data-far-smart-checkbox]');
          const variant = row.querySelector('[data-far-smart-variant]');
          if (check?.checked && variant?.value) {
            addOptional(variant.value, check.dataset.farSmartQty);
          }
        });
        const benchSelector = this.form.querySelector('[data-far-smart-bench]:checked');
        if (benchSelector?.value) addOptional(benchSelector.value, 1);
        const barSelector = this.form.querySelector('[data-far-smart-bar]:checked');
        if (barSelector?.value) addOptional(barSelector.value, 1);
        this.form.querySelectorAll('[data-far-smart-pairs]').forEach(pairSelect => {
          const pairs = Number(pairSelect.value);
          if (Number.isInteger(pairs) && pairs > 0) {
            addOptional(pairSelect.dataset.farSmartPairVariant, pairs * 2);
          }
        });
        this.form.querySelectorAll('[data-far-floor-option]').forEach(floorCard => {
          const quantityField = floorCard.querySelector('[data-far-floor-qty]');
          const variantField = floorCard.querySelector('[data-far-floor-variant]');
          const units = Number(quantityField?.value);
          if (Number.isInteger(units) && units > 0 && units <= 100 && variantField?.value) {
            addOptional(variantField.value, units);
          }
        });

        let fetchRequestOpts;
        if (additions.length) {
          const primary = { id: primaryId, quantity: requestedQuantity };
          const sellingPlan = formData.get('selling_plan');
          if (sellingPlan) primary.selling_plan = Number(sellingPlan);
          const properties = {};
          for (const [name, value] of formData.entries()) {
            const propertyMatch = /^properties\[(.*)\]$/.exec(name);
            if (propertyMatch) properties[propertyMatch[1]] = value;
          }
          if (Object.keys(properties).length) primary.properties = properties;
          fetchRequestOpts = {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items: [primary, ...additions],
              sections,
              sections_url: window.location.pathname
            })
          };
        } else {
          // Preserve the theme's original add-to-cart behavior when no add-on is selected.
          formData.append('sections_url', window.location.pathname);
          formData.append('sections', sections);
          fetchRequestOpts = {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          };
        }

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        if (!response.ok || data.status) {
          this.setErrorMsgState(data.description || data.message || 'Unable to add products');
          throw new Error(data.description || 'Unable to add products');
        }
        const cartUiResponse = data;

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(cartUiResponse);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                cartUiResponse,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) cartItems.refreshCart();
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.find(item => Number(item.variant_id) === primaryId);
        const itemInNewCart = newCartData.items.find(item => Number(item.variant_id) === primaryId);

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: primaryId,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: itemInNewCart ? itemInNewCart.quantity : itemInOldCart.quantity + requestedQuantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: primaryId
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.setErrorMsgState(this.errorMsg?.textContent || (document.documentElement.lang.startsWith('fr') ? "Impossible d'ajouter les produits au panier." : "Could not add products to cart."));
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent
          }
        }));
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) this.errorMsg.textContent = error;
    }
  }

  customElements.define('product-form', ProductForm);
}
